<template>
  <div class="trial-create-view">
    <AppHeader hide-main-nav hide-utility-actions />

    <main class="trial-create-main">
      <section class="trial-create-hero">
        <p class="trial-kicker text-center">ログイン不要のお試し</p>
        <div class="trial-hero-heading text-center">
          <h1>今の状態から、旅行づくりを始める</h1>
        </div>

        <div class="trial-layout">
          <div class="trial-left">
            <section class="trial-template-section">
              <div class="trial-template-heading">
                <span class="trial-option-kicker">テンプレートから作成</span>
                <h2>人気の旅程から選ぶ</h2>
              </div>

              <div class="trial-template-grid">
                <button
                  v-for="template in shioriTemplates"
                  :key="template.id"
                  :data-testid="`trial-template-${template.id}`"
                  class="trial-template-card"
                  type="button"
                  :aria-label="`${template.title}（${template.days}日間）を選ぶ`"
                  :disabled="creatingTemplateId === template.id"
                  @click="openTemplateCreate(template)"
                >
                  <span class="template-card-image-wrap">
                    <img
                      class="template-card-image"
                      :src="template.imageUrl"
                      :alt="template.imageAlt"
                      width="1600"
                      height="900"
                      loading="lazy"
                      decoding="async"
                      @error="hideBrokenTemplateImage"
                    />
                  </span>
                  <span class="template-card-main">
                    <strong>{{ template.title }}</strong>
                    <small>{{ template.description }}</small>
                  </span>
                  <span class="template-card-meta">
                    {{
                      creatingTemplateId === template.id
                        ? "作成中..."
                        : `${template.days}日間`
                    }}
                  </span>
                </button>
              </div>
            </section>
          </div>

          <div class="trial-right">
            <div class="card blank-create-card">
              <h2>自由に作成する</h2>
              <p class="blank-desc">白紙から自由に作ることもできます。</p>
              <button
                class="btn btn-primary btn-lg w-full trial-blank-button"
                type="button"
                @click="openBlankCreate"
              >
                白紙から作成
              </button>
            </div>
          </div>
        </div>
      </section>
    </main>

    <CreateShioriModal
      v-model="showModal"
      :prefill-data="activePrefillData"
      @create="goToTrialShiori"
      @template-create-failed="onTemplateCreateFailed"
    />
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import { useRouter } from "vue-router";
import AppHeader from "@/components/AppHeader.vue";
import CreateShioriModal from "@/components/CreateShioriModal.vue";
import { shioriTemplates } from "@/data/shioriTemplates.js";
import { useScheduleStore } from "@/store/schedule-store.js";
import { useShioriMemberStore } from "@/store/shiori-member-store.js";
import { useShioriStore } from "@/store/shiori-store.js";
import {
  createTemplateSchedulePayloads,
  createTemplateSchedulesWithRollback,
} from "@/utils/shiori-template.js";

const router = useRouter();
const scheduleStore = useScheduleStore();
const shioriMemberStore = useShioriMemberStore();
const shioriStore = useShioriStore();

const showModal = ref(false);
const pendingTemplate = ref(null);
const creatingTemplateId = ref(null);

const toLocalDateKey = (date) =>
  [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");

const addDays = (dateKey, days) => {
  const [year, month, day] = dateKey.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  date.setDate(date.getDate() + days);
  return toLocalDateKey(date);
};

const activePrefillData = computed(() => {
  const template = pendingTemplate.value;
  if (!template) return null;

  const startDate = addDays(toLocalDateKey(new Date()), 30);
  const endDate = addDays(startDate, template.days - 1);
  return {
    title: template.title,
    startDate,
    endDate,
    days: template.days,
    budget: template.budget,
    destinationType: template.destinationType,
    destinations: template.destinations,
    members: [{ name: "あなた", email: "", appUserCode: null }],
    isTemplate: true,
    templateImageUrl: template.imageUrl,
    templateImageAlt: template.imageAlt,
  };
});

const resolveCreatedShioriId = (result, context = {}) =>
  context.shioriId ||
  result?.SHIORI_NOV ||
  result?.shiori_id ||
  result?.data?.SHIORI_NOV;

const createTemplateSchedules = async (template, shioriId, startDate) => {
  if (!template || !shioriId || !startDate) return;

  const members = await shioriMemberStore.fetchMembers(shioriId, {
    force: true,
  });
  const payloads = createTemplateSchedulePayloads({
    template, shioriId, startDate, members: members || [],
  });
  await createTemplateSchedulesWithRollback({
    payloads,
    createSchedule: (payload) => scheduleStore.createSchedule(payload),
    rollback: () => shioriStore.deleteShiori(shioriId),
  });

  try {
    await shioriStore.updateShiori(shioriId, {
      imageUrl: template.imageUrl,
      imageAlt: template.imageAlt,
    });
  } catch (error) {
    console.error("お試しテンプレート画像更新エラー:", error);
    alert("しおりは作成されましたが、テンプレート画像を設定できませんでした。画像はしおり管理から追加できます。");
  }
};

const hideBrokenTemplateImage = (event) => {
  event.currentTarget.closest(".template-card-image-wrap")?.classList.add("is-error");
};

const openTemplateCreate = (template) => {
  pendingTemplate.value = template;
  showModal.value = true;
};

const openBlankCreate = () => {
  pendingTemplate.value = null;
  showModal.value = true;
};

const onTemplateCreateFailed = async ({ shioriId, error }) => {
  try {
    await shioriStore.deleteShiori(shioriId);
    alert("テンプレートを完全に作成できなかったため、作成内容を取り消しました。もう一度お試しください。");
  } catch (rollbackError) {
    console.error("テンプレートしおりのロールバックエラー:", rollbackError);
    alert("作成に失敗し、一部データが残った可能性があります。マイページまたはゴミ箱を確認してください。");
  } finally {
    console.error("テンプレート作成前処理エラー:", error);
    pendingTemplate.value = null;
    creatingTemplateId.value = null;
  }
};

const goToTrialShiori = async (result, context = {}) => {
  const template = pendingTemplate.value;
  const shioriId = resolveCreatedShioriId(result, context);
  if (!shioriId) return;

  try {
    if (template) {
      creatingTemplateId.value = template.id;
      await createTemplateSchedules(template, shioriId, context.startDate);
    }
  } catch (error) {
    console.error("お試しテンプレート予定作成エラー:", error);
    alert(error.rollbackError
      ? "作成に失敗し、一部データが残った可能性があります。マイページまたはゴミ箱を確認してください。"
      : "テンプレートを完全に作成できなかったため、作成内容を取り消しました。もう一度お試しください。");
    pendingTemplate.value = null;
    creatingTemplateId.value = null;
    return;
  } finally {
    pendingTemplate.value = null;
    creatingTemplateId.value = null;
  }
  router.push(`/trial/${shioriId}`);
};
</script>

<style scoped>
.trial-create-view {
  min-height: 100vh;
  background: var(--color-background);
}

.trial-create-main {
  width: min(1080px, 100%);
  margin: 0 auto;
  padding: clamp(3rem, 8vw, 6rem) 1.5rem;
}

.trial-create-hero {
  padding: clamp(2rem, 6vw, 4rem);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xl);
  box-shadow: var(--card-shadow);
}

.trial-kicker {
  margin: 0 0 0.75rem;
  color: #5f7f51;
  font-size: 0.78rem;
  font-weight: 900;
  letter-spacing: 0;
  text-transform: uppercase;
}

.trial-hero-heading {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 1.25rem;
  align-items: start;
}

.trial-create-hero h1 {
  margin: 0 0 1rem;
  color: var(--color-text);
  font-size: clamp(2rem, 5vw, 3.5rem);
  line-height: 1.12;
}

.trial-create-hero p {
  max-width: 680px;
  margin: 0;
  color: #5b6472;
  line-height: 1.85;
}

.trial-blank-button {
  padding: 0.85rem 1.15rem;
  border: 1px solid #d8e3d1;
  border-radius: 0.5rem;
  background: var(--color-surface);
  color: #37512d;
  cursor: pointer;
  font-weight: 800;
}

.trial-hero-heading {
  margin-bottom: 2rem;
}

.trial-layout {
  display: flex;
  gap: 2rem;
  align-items: stretch;
}

.trial-left {
  flex: 6;
}

.trial-right {
  flex: 4;
  display: flex;
}

.blank-create-card {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: 2.5rem 2rem;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-sm);
}

.blank-create-card h2 {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--color-text);
  margin-bottom: 0.5rem;
}

.blank-desc {
  color: var(--color-text-muted);
  font-size: 0.875rem;
  margin-bottom: 1.5rem;
}

.trial-template-section {
  margin-top: 0;
}

.trial-template-heading {
  margin-bottom: 1rem;
}

.trial-template-heading h2 {
  margin: 0.25rem 0 0;
  color: var(--color-text);
  font-size: 1.25rem;
}

.trial-option-kicker {
  color: var(--color-primary-ink);
  font-size: 0.78rem;
  font-weight: 800;
}

.trial-template-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 0.9rem;
}

.trial-template-card {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 0.85rem;
  align-items: center;
  padding: 1rem;
  text-align: left;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  background: var(--color-background);
  color: var(--color-text);
  cursor: pointer;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease,
    border-color 0.2s ease;
}

.template-card-image-wrap {
  grid-column: 1 / -1;
  overflow: hidden;
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: 0.4rem;
  background: #eef7e8;
}

.template-card-image-wrap.is-error {
  display: none;
}

.template-card-image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.trial-template-card:hover:not(:disabled) {
  transform: translateY(-2px);
  border-color: #76a866;
  box-shadow: 0 12px 28px rgba(54, 77, 43, 0.14);
}

.trial-template-card:focus-visible {
  outline: 3px solid #3f712f;
  outline-offset: 4px;
  border-color: #3f712f;
  box-shadow: 0 0 0 2px #f9fcf6, 0 12px 28px rgba(54, 77, 43, 0.18);
}

.trial-template-card:disabled {
  cursor: wait;
  opacity: 0.7;
}

.template-card-main {
  display: grid;
  gap: 0.25rem;
  min-width: 0;
}

.template-card-main strong {
  overflow: hidden;
  color: var(--color-text);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.template-card-main small {
  color: #5b6472;
  line-height: 1.5;
}

.template-card-meta {
  color: #5f7f51;
  font-size: 0.82rem;
  font-weight: 800;
  white-space: nowrap;
}

@media (max-width: 768px) {
  .trial-layout {
    flex-direction: column;
    gap: 1.5rem;
  }

  .trial-hero-heading,
  .trial-template-card {
    grid-template-columns: 1fr;
  }

  .trial-blank-button {
    width: 100%;
  }

  .template-card-meta {
    white-space: normal;
  }
}
</style>
